import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { CompanyReadScope } from "contexts/CompanyReadScope";
import { useTranslation } from "react-i18next";
import { Inline, Stack } from "components/layout/reusableLayouts";
import TransactionsTable, {
  TransactionFilterCriteria,
} from "components/common/transactions/TransactionsTable";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";
import CardsTable from "components/common/cards/CardsTable";
import { CardDetailsDrawer } from "components/common/cards/CardDetailsDrawer";
import TransactionsType from "enums/TransactionsType";
import CardStatusType from "enums/CardStatusType";
import { TransactionExport } from "./TransactionExport";
import dayjs from "utils/dayjs";
import { CompanyAccounts } from "./CompanyAccounts";
import { CompanyDashboard } from "./CompanyDashboard";
import { CompanyActivity } from "./CompanyActivity";
import { CompanyLedger } from "./CompanyLedger";
import { dateFilter } from "./types";

export function CompanyConsole({
  companyId,
  tab,
  defaultCurrency = "USD",
  showLedger = false,
}: {
  companyId: string;
  tab: string;
  defaultCurrency?: string;
  showLedger?: boolean;
}) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [refresh, setRefresh] = useState(0);
  const [updatedAt, setUpdatedAt] = useState<number>();
  const displayParams = useMemo(() => {
    const next = new URLSearchParams(params);
    if (tab === "dashboard") {
      if (!next.get("from"))
        next.set("from", dayjs().subtract(29, "day").format("YYYY-MM-DD"));
      if (!next.get("to")) next.set("to", dayjs().format("YYYY-MM-DD"));
      next.set(
        "currency",
        (next.get("currency") || defaultCurrency).toUpperCase(),
      );
    }
    return next;
  }, [params, tab, defaultCurrency]);
  const navigate = (values: Record<string, string | null>) =>
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      Object.entries(values).forEach(([key, value]) =>
        value == null || value === "" ? next.delete(key) : next.set(key, value),
      );
      if (next.get("tab") !== "ledger") next.delete("ledgerAccount");
      return next;
    });
  const financialAccountId = params.get("financialAccount") || undefined;
  const transactionId = params.get("transaction");
  const cardId = params.get("card");
  const filters = useMemo<TransactionFilterCriteria>(
    () => ({
      ...dateFilter(params),
      accountId: financialAccountId,
      searchTerm: params.get("search") || undefined,
      type: params.get("type")
        ? [params.get("type") as TransactionsType]
        : undefined,
      status: params.get("status")?.split(","),
      currency: params.get("currency") || undefined,
      cardId: params.get("cardFilter") || undefined,
      metric: params.get("metric") || undefined,
      issuer: params.get("issuer") || undefined,
      amountRange: {
        min: params.get("min") ? Number(params.get("min")) : undefined,
        max: params.get("max") ? Number(params.get("max")) : undefined,
      },
    }),
    [params, financialAccountId],
  );
  const props = { companyId, params, navigate };
  return (
    <CompanyReadScope.Provider value={companyId}>
      <Stack gap="var(--space-4)" sx={{ flex: 1, minHeight: 0 }}>
        {(tab === "transactions" ||
          (tab === "dashboard" && updatedAt != null)) && (
          <Inline justify="end" gap="var(--space-3)" sx={{ flexShrink: 0 }}>
            {tab === "dashboard" && updatedAt != null && (
              <Typography variant="caption">
                {t("Updated")}{" "}
                {new Date(updatedAt).toLocaleTimeString(undefined, {
                  hour: "numeric",
                  minute: "2-digit",
                  timeZoneName: "short",
                })}
              </Typography>
            )}
            {tab === "transactions" && (
              <TransactionExport companyId={companyId} filters={filters} />
            )}
          </Inline>
        )}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {tab === "dashboard" && (
            <CompanyDashboard
              key={JSON.stringify([
                ["from", "to", "currency", "financialAccount", "issuer"].map(
                  (key) => displayParams.get(key),
                ),
                refresh,
              ])}
              {...props}
              params={displayParams}
              onUpdated={setUpdatedAt}
            />
          )}
          {tab === "accounts" && (
            <CompanyAccounts key={refresh} {...props} showLedger={showLedger} />
          )}
          {tab === "transactions" && (
            <TransactionsTable
              selectedCompanyId={companyId}
              filterCriteria={filters}
              refreshTransactions={refresh}
              onRowClick={(id) => navigate({ transaction: id })}
            />
          )}
          {tab === "cards" && (
            <CardsTable
              selectedCompanyId={companyId}
              financialAccountId={financialAccountId}
              issuer={params.get("issuer") || undefined}
              currency={params.get("currency") || undefined}
              refreshCards={refresh}
              filterData={{
                cardStatus: params.get("status")
                  ? [params.get("status") as CardStatusType]
                  : [],
                cardType: [],
                userId: null,
                searchTerm: params.get("search") || "",
              }}
              handleOpenCardDetails={(id) => navigate({ card: id })}
            />
          )}
          {tab === "ledger" && <CompanyLedger {...props} />}
          {tab === "activity" && <CompanyActivity {...props} />}
        </Box>
        {transactionId && (
          <TransactionDetailsDrawer
            key={`${companyId}:${transactionId}`}
            open
            transactionId={transactionId}
            onViewActivity={() =>
              navigate({
                tab: "activity",
                activity: "events",
                resource: transactionId,
                transaction: null,
              })
            }
            selectedCompanyId={companyId}
            handleCloseDrawer={() => navigate({ transaction: null })}
            handleRefreshTransactions={() => setRefresh((value) => value + 1)}
          />
        )}
        {cardId && (
          <CardDetailsDrawer
            key={`${companyId}:${cardId}`}
            open
            cardId={cardId}
            onViewTransactions={() =>
              navigate({
                tab: "transactions",
                cardFilter: cardId,
                card: null,
                metric: null,
                search: null,
              })
            }
            selectedCompanyId={companyId}
            onClose={(changed) => {
              navigate({ card: null });
              if (changed) setRefresh((value) => value + 1);
            }}
          />
        )}
      </Stack>
    </CompanyReadScope.Provider>
  );
}
